<template>
    <div>
        <!-- Student's Own Event Applications Table -->
        <div v-if="checkPermission('coopeventapplication_me')" class="mb-5">
            <h4 class="mb-3 font-weight-bold">{{ $t('coop_my_applications') }}</h4>
            <datatable ref="myApplicationsTable"
                       :isLoading.sync="myApplicationsTable.isLoading"
                       :columns="myApplicationsTable.columns"
                       :rows="myApplicationsTable.rows"
                       :total="myApplicationsTable.total"
                       :queryParams="myApplicationsTable.queryParams"
                       :lineNumbers="false"
                       @on-page-change="onMyApplicationsPageChange"
                       @on-sort-change="onMyApplicationsSortChange"
                       @on-per-page-change="onMyApplicationsPerPageChange"
                       v-show="myApplicationsTable.showTable"
            />
        </div>

        <!-- Active Events Table -->
        <div>
            <h4 class="mb-3 font-weight-bold">{{ $t('coop_events') }}</h4>
            <datatable ref="datatable"
                       :isLoading.sync="datatable.isLoading"
                       :columns="datatable.columns"
                       :rows="datatable.rows"
                       :total="datatable.total"
                       :queryParams="datatable.queryParams"
                       :lineNumbers="false"
                       @on-page-change="onPageChange"
                       @on-sort-change="onSortChange"
                       @on-per-page-change="onPerPageChange"
                       v-show="datatable.showTable"
            />
        </div>
    </div>
</template>

<script>
    //Component
    import DatatableFilter from '@/components/datatable/DatatableFilter';
    import Datatable from '@/components/datatable/Datatable';
    import ParameterSelectbox from '@/components/interactive-fields/ParameterSelectbox';
    import CoopCompanySelectbox from '@/components/interactive-fields/CoopCompanySelectbox.vue';

    //Service
    import CoopEventService from '@/services/CoopEventService';
    import CoopEventApplicationService from '@/services/CoopEventApplicationService';

    //Other
    import qs from 'qs';

    export default {
        name: 'Events',
        components: {
            DatatableFilter,
            Datatable,
            ParameterSelectbox,
            CoopCompanySelectbox
        },
        data() {
            return {
                myApplicationsTable: {
                    isLoading: false,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('delete'),
                                    class: 'ri-delete-bin-line text-danger',
                                    permission: 'coopeventapplication_delete',
                                    callback: (row) => {
                                        this.deleteMyApplication(row);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('coop_event_name'),
                            field: 'event_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_company'),
                            field: 'company_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('date'),
                            field: 'event_date',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_event_time'),
                            field: 'start_time',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('status'),
                            field: this.getLocaleField('status_name'),
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: '-id',
                        page: 1,
                        limit: 20
                    }
                },
                datatable: {
                    isLoading: false,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('coop_event_join'),
                                    class: 'ri-checkbox-circle-line',
                                    callback: (row) => {
                                        this.applyEvent(row);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('coop_event_name'),
                            field: 'name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_company'),
                            field: 'company_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_event_type'),
                            field: this.getLocaleField('type_name'),
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('date'),
                            field: 'event_date',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_event_time'),
                            field: 'start_time',
                            formatFn: (val, row) => (row.start_time && row.end_time) ? `${row.start_time.substring(0, 5)} - ${row.end_time.substring(0, 5)}` : '',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_event_format'),
                            field: this.getLocaleField('format_name'),
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_quota'),
                            field: 'quota',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('remaining_quota'),
                            field: 'remaining_quota',
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: '-id',
                        page: 1,
                        limit: 20
                    }
                }
            };
        },
        created() {
            this.getMyApplications();
            this.getRows();
        },
        methods: {
            getMyApplications() {
                if (!this.checkPermission('coopeventapplication_me')) {
                    return;
                }
                this.myApplicationsTable.isLoading = true;
                const config = {
                    params: { ...this.myApplicationsTable.queryParams },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                CoopEventApplicationService.getMe(config)
                    .then((response) => {
                        this.myApplicationsTable.rows = response.data?.data || response.data || [];
                        this.myApplicationsTable.total = response.data?.pagination?.total || this.myApplicationsTable.rows.length;
                        this.myApplicationsTable.showTable = true;
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    })
                    .finally(() => {
                        this.myApplicationsTable.isLoading = false;
                    });
            },
            onMyApplicationsPageChange(params) {
                this.myApplicationsTable.queryParams.page = params.currentPage;
                this.getMyApplications();
            },
            onMyApplicationsPerPageChange(params) {
                this.myApplicationsTable.queryParams.limit = params.currentPerPage;
                this.myApplicationsTable.queryParams.page = 1;
                this.getMyApplications();
            },
            onMyApplicationsSortChange(params) {
                if (params && params.length > 0) {
                    const sortType = params[0].type === 'desc' ? '-' : '';
                    this.myApplicationsTable.queryParams.sort = sortType + params[0].field;
                }
                this.getMyApplications();
            },
            deleteMyApplication(row) {
                this.$swal.fire({
                    text: this.$t('coop_event_application_delete_confirm'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no')
                }).then((result) => {
                    if (result.isConfirmed) {
                        CoopEventApplicationService.remove(row.id)
                            .then((response) => {
                                this.$toast.success(this.$t('api.' + response.data.message));
                                this.getMyApplications();
                                this.getRows();
                            })
                            .catch((error) => {
                                this.showErrors(error, null);
                            });
                    }
                });
            },
            filter() {
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            filterClear() {
                this.datatable.queryParams.filter = {};
                this.getRows();
            },
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            onSortChange(params) {
                if (params && params.length > 0) {
                    const sortType = params[0].type === 'desc' ? '-' : '';
                    this.datatable.queryParams.sort = sortType + params[0].field;
                }
                this.getRows();
            },
            getRows() {
                this.datatable.showTable = true;
                this.datatable.isLoading = true;

                const config = {
                    params: {
                        ...this.datatable.queryParams
                    },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                return CoopEventService.getActive(config)
                    .then((response) => {
                        this.datatable.rows = response.data?.data || response.data || [];
                        this.datatable.total = response.data?.pagination?.total || this.datatable.rows.length;
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    })
                    .finally(() => {
                        this.datatable.isLoading = false;
                    });
            },
            applyEvent(row) {
                this.$swal.fire({
                    text: this.$t('coop_event_apply_confirm'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no')
                }).then((result) => {
                    if (result.isConfirmed) {
                        CoopEventApplicationService.apply(row.id)
                            .then((response) => {
                                this.$toast.success(this.$t('api.' + response.data.message));
                                this.getMyApplications();
                                this.getRows();
                            })
                            .catch((error) => {
                                this.showErrors(error, null);
                            });
                    }
                });
            }
        }
    };
</script>
